import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { deckKeys, reviewQueueKey } from '@/entities/deck';
import { deleteDeck, updateDeck } from '@/shared/api/decks';
import type { Deck } from '@/shared/api/types';
import { Button, ErrorMessage, TextArea, TextField } from '@/shared/ui';

import styles from './DeckEditor.module.css';

/** Renames a deck and changes its description. */
export function DeckGeneralForm({ deck }: { deck: Deck }): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(deck.title);
  const [description, setDescription] = useState(deck.description);

  const mutation = useMutation({
    mutationFn: () => updateDeck(deck.id, { title, description }),
    onSuccess: async (updated) => {
      queryClient.setQueryData(deckKeys.one(deck.id), updated);
      await queryClient.invalidateQueries({ queryKey: deckKeys.all, exact: true });
    },
  });

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    mutation.mutate();
  };

  return (
    <form className={styles.form} onSubmit={submit}>
      <TextField
        label={t('decks.name')}
        required
        maxLength={200}
        value={title}
        onChange={(event) => {
          setTitle(event.target.value);
        }}
      />
      <TextArea
        label={t('decks.description')}
        maxLength={2000}
        value={description}
        onChange={(event) => {
          setDescription(event.target.value);
        }}
      />
      {mutation.isError && <ErrorMessage error={mutation.error} />}
      {mutation.isSuccess && (
        <p className={styles.ok} role="status">
          {t('common.saved')}
        </p>
      )}
      <Button type="submit" className={styles.submit} disabled={mutation.isPending}>
        {t('common.save')}
      </Button>
    </form>
  );
}

function squash(text: string): string {
  return text.trim().replace(/\s+/g, ' ');
}

/**
 * Deletes a deck with all its cards. As on GitHub, the name has to be typed
 * first, so that a deck is never lost to a stray click.
 */
export function DeckDangerZone({
  deck,
  onDeleted,
}: {
  deck: Deck;
  onDeleted: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');

  const mutation = useMutation({
    mutationFn: () => deleteDeck(deck.id),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: deckKeys.one(deck.id) });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: deckKeys.all, exact: true }),
        queryClient.invalidateQueries({ queryKey: reviewQueueKey }),
      ]);
      onDeleted();
    },
  });

  return (
    <div className={styles.danger}>
      <div className={styles.dangerRow}>
        <div>
          <p className={styles.dangerTitle}>{t('decks.deleteDeck')}</p>
          <p className={styles.dangerText}>{t('decks.deleteDeckText')}</p>
        </div>
        {!open && (
          <Button
            variant="danger"
            onClick={() => {
              setOpen(true);
            }}
          >
            {t('decks.deleteDeck')}
          </Button>
        )}
      </div>
      {open && (
        <form
          className={styles.form}
          onSubmit={(event) => {
            event.preventDefault();
            mutation.mutate();
          }}
        >
          <TextField
            label={t('decks.deleteDeckConfirm', { title: deck.title })}
            autoComplete="off"
            value={typed}
            onChange={(event) => {
              setTyped(event.target.value);
            }}
          />
          {mutation.isError && <ErrorMessage error={mutation.error} />}
          <Button
            type="submit"
            variant="danger"
            disabled={squash(typed) !== squash(deck.title) || mutation.isPending}
          >
            {t('decks.deleteDeckForever')}
          </Button>
        </form>
      )}
    </div>
  );
}
